// Which raster tile provider serves the basemaps, and under which licence.
//
// This is one module because the answer must be the same for both map engines. The WebGL engine and
// the Leaflet fallback each used to name their own tile URLs, which is how the fallback kept serving
// a non-commercial basemap after the main engine stopped — a breach that only appears on machines
// without WebGL, i.e. never on the developer's.
//
// Two doors to the same imagery, on different terms:
//
//   keyless   `server.arcgisonline.com` answers without authentication, but Esri's item terms
//             require an ArcGIS Online or Enterprise licence and exclude commercial use. Fine for a
//             local tool, not for a public site.
//   licensed  the ArcGIS Static Basemap Tiles service, which the Location Platform terms permit in
//             a commercial application when requests carry an access token from your own
//             subscription. 2M basemap tiles/month free, then $0.15 per 1,000.
//
// `VITE_ARCGIS_KEY` picks between them, so drone-player keeps its current behaviour with no env set
// and a public build fails toward the licensed source rather than shipping the wrong one.

export const ARCGIS_KEY: string | undefined =
  (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_ARCGIS_KEY || undefined;

/** True when this build is licensed for commercial use of the raster basemaps. */
export const licensedRasters = (): boolean => !!ARCGIS_KEY;

export interface RasterBasemap {
  /** Tile URL template. ArcGIS orders the path `{z}/{y}/{x}`, which both engines accept literally. */
  url: string;
  /** 512 for the licensed service, 256 for everything else — mixing them misregisters imagery by a
   *  whole zoom level, which reads as "the satellite is slightly off" rather than as an error. */
  tileSize: number;
  maxZoom: number;
  attribution: string;
}

const staticTiles = (style: string): string =>
  `https://static-map-tiles-api.arcgis.com/arcgis/rest/services/static-basemap-tiles-service/v1/arcgis/${style}/static/tile/{z}/{y}/{x}?token=${ARCGIS_KEY}`;

/** Satellite imagery. Capped at z18 because Esri has real tiles only to ~z18 over rural Türkiye —
 *  MapLibre then overzooms the last real tile instead of showing a "not available" placeholder. */
export const satelliteRaster = (): RasterBasemap =>
  ARCGIS_KEY
    ? { url: staticTiles('imagery'), tileSize: 512, maxZoom: 18, attribution: 'Powered by Esri · Imagery © Esri, Maxar, Earthstar Geographics' }
    : { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', tileSize: 256, maxZoom: 18, attribution: 'Imagery © Esri' };

/**
 * Streets, for the RASTER path only.
 *
 * The WebGL engine does not use this: it draws streets from OpenFreeMap vector tiles, which permit
 * commercial use with no key and no metering, so paying per tile for the same picture would be
 * money spent on nothing. Only the no-WebGL fallback needs a raster, and there the licensed door is
 * the ArcGIS one — which is why setting the key removes the last non-commercial basemap from the
 * build rather than only the imagery.
 */
export const streetsRaster = (): RasterBasemap =>
  ARCGIS_KEY
    ? { url: staticTiles('streets'), tileSize: 512, maxZoom: 19, attribution: 'Powered by Esri · © Esri, TomTom, Garmin, OpenStreetMap contributors' }
    : { url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png', tileSize: 256, maxZoom: 20, attribution: '© OpenStreetMap contributors © CARTO' };
